import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const WEEKDAY_LABELS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

// Kurse mit eigenem, mitgliedergeschuetztem Lernportal (siehe protectCampRoute in server.js)
const COURSE_PORTALS = {
  'scratch-kurs': '/scratch-kurs/index.html',
  'ki-kurs': '/ki-kurs/index.html',
  'python-kurs': '/python-kurs/index.html'
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
}

function MemberDashboard() {
  const { user, authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    authFetch('/api/members/my-progress')
      .then(async (res) => {
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Fehler beim Laden');
        setData(result.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  const displayName = data?.child?.first_name || user?.name || user?.username || '';

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-9">
        <h4 className="mb-4">Willkommen{displayName ? `, ${displayName}` : ''}!</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        {!error && (!data?.myCourses || data.myCourses.length === 0) && (!data?.courses || data.courses.length === 0) && (
          <div className="alert alert-info">
            Hier erscheinen eure Kurse, sobald ihr freigeschaltet seid.
          </div>
        )}

        {data?.myCourses?.map(course => {
          const notesForCourse = data?.courses?.find(c => c.course_slug === (course.slug || course.name));
          const portalUrl = COURSE_PORTALS[course.slug || course.name];
          return (
            <div key={course.id} className="card mb-4" style={course.color ? { borderTopColor: course.color, borderTopWidth: 3 } : undefined}>
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0 text-white">{course.name}</h5>
                  {course.slots?.length > 0 && (
                    <small>
                      {course.slots.map(s => `${WEEKDAY_LABELS[s.weekday]} ${s.time.slice(0, 5)} Uhr`).join(', ')}
                    </small>
                  )}
                </div>
                {portalUrl && (
                  <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                    Kurs öffnen
                  </a>
                )}
              </div>
              <div className="card-body">
                {course.description && <p className="mb-3">{course.description}</p>}

                {notesForCourse?.notes.map((n, i) => (
                  <div
                    key={i}
                    className={i < notesForCourse.notes.length - 1 ? 'mb-3 pb-3 border-bottom' : 'mb-0'}
                  >
                    <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                      <strong>{n.unit_label}</strong>
                      {n.unit_date && <small className="text-muted">{formatDate(n.unit_date)}</small>}
                    </div>
                    {n.thema && <p className="mb-2 mt-1">{n.thema}</p>}
                    {n.ueben && (
                      <div className="alert alert-warning py-2 px-3 mb-0">
                        <strong>Übungsaufgabe:</strong> {n.ueben}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Lernfortschritt-Notizen zu Kursen ohne (oder mit abweichendem) Kursnamen */}
        {data?.courses?.filter(course => !data?.myCourses?.some(mc => (mc.slug || mc.name) === course.course_slug)).map(course => (
          <div key={course.course_slug} className="card mb-4">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0 text-white">{course.course_slug}</h5>
              {COURSE_PORTALS[course.course_slug] && (
                <a href={COURSE_PORTALS[course.course_slug]} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                  Kurs öffnen
                </a>
              )}
            </div>
            <div className="card-body">
              {course.notes.map((n, i) => (
                <div
                  key={i}
                  className={i < course.notes.length - 1 ? 'mb-3 pb-3 border-bottom' : 'mb-0'}
                >
                  <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                    <strong>{n.unit_label}</strong>
                    {n.unit_date && <small className="text-muted">{formatDate(n.unit_date)}</small>}
                  </div>
                  {n.thema && <p className="mb-2 mt-1">{n.thema}</p>}
                  {n.ueben && (
                    <div className="alert alert-warning py-2 px-3 mb-0">
                      <strong>Übungsaufgabe:</strong> {n.ueben}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MemberDashboard;
